import classNames from 'classnames';
import * as React from 'react';

import {
  Drawer,
  DrawerPosition,
  DrawerSize,
} from 'src/components/drawer/Drawer';
import { Panel, PanelStack } from 'src/components/panelStack/PanelStack';
import { DrawerHeaderTitle } from 'src/components/resourceDialog/DrawerHeaderTitle';

import styles from 'src/components/resourceDialog/styles/panelStackDrawer.module.scss';

export type PanelStackDrawerProps<TContext> = {
  /** The context value to provide to children */
  contextValue: TContext;
  /** The React context provider component */
  ContextProvider: React.Provider<TContext>;
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Handler called when the drawer is closed */
  onClose: () => void;
  /** The initial panel to render in the stack */
  initialPanel: Panel<object>;
  /** Whether the footer should be shown */
  hasFooterOptions: boolean;
  /** The footer component to render */
  FooterComponent?: React.VFC;
  /** The dialog title */
  title: string;
  /** Handler for the back button in the header, null to hide */
  headerBackButton?: (() => void) | null;
  /** Test ID for the back button */
  headerBackButtonTestId?: string;
  /** Whether to expand the drawer to full size */
  fullSize?: boolean;
  /** Additional content to render inside the drawer (e.g., conditional content) */
  children?: React.ReactNode;
  /** Whether to show the close button */
  isCloseButtonShown?: boolean;
  /** Additional CSS class name for the drawer */
  className?: string;
};

/**
 * A generic drawer component with a PanelStack for multi-step dialogs.
 *
 * This component consolidates the common pattern used across:
 * - ProjectResourceDialog
 * - ProjectVirtualGraphPreviewDialog
 * - ProjectFileUploadPreviewDialog
 *
 * It provides:
 * - A context provider wrapper
 * - A drawer with consistent styling
 * - A header with optional back button
 * - A PanelStack for multi-step navigation
 * - An optional footer
 *
 * @example
 * ```tsx
 * <PanelStackDrawer
 *   contextValue={contextValue}
 *   ContextProvider={MyDialogContext.Provider}
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   initialPanel={initialPanel}
 *   hasFooterOptions={hasFooterOptions}
 *   FooterComponent={MyDialogFooter}
 *   title={contextValue.dialogTitle}
 *   headerBackButton={contextValue.headerBackButton}
 *   fullSize={contextValue.fullSize}
 * />
 * ```
 */
export const PanelStackDrawer = <TContext,>({
  contextValue,
  ContextProvider,
  isOpen,
  onClose,
  initialPanel,
  hasFooterOptions,
  FooterComponent,
  title,
  headerBackButton,
  headerBackButtonTestId,
  fullSize = false,
  children,
  isCloseButtonShown = true,
  className,
}: PanelStackDrawerProps<TContext>): React.ReactElement => {
  const headerTitle = (
    <DrawerHeaderTitle
      backButtonTestId={headerBackButtonTestId}
      onBack={headerBackButton}
      title={title}
    />
  );

  const drawerContent = children ?? (
    <>
      <div className={styles.drawerBody}>
        <PanelStack initialPanel={initialPanel} renderActivePanelOnly />
      </div>
      {hasFooterOptions && FooterComponent ? (
        <div className={styles.drawerFooter}>
          <FooterComponent />
        </div>
      ) : null}
    </>
  );

  return (
    <ContextProvider value={contextValue}>
      <Drawer
        autoFocus={false}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        className={classNames(styles.drawerProjectResource, className)}
        enforceFocus={false}
        hasBackdrop
        isCloseButtonShown={isCloseButtonShown}
        isOpen={isOpen}
        onClose={onClose}
        position={DrawerPosition.RIGHT}
        size={fullSize ? DrawerSize.FULL : DrawerSize.MD}
        title={headerTitle}
      >
        {drawerContent}
      </Drawer>
    </ContextProvider>
  );
};
